import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Sparkles } from "lucide-react";
import { api } from "../api";
import { ConfirmDialog } from "../ConfirmDialog";
import { UploadZone } from "../../components/common/UploadZone";
import { fileUrl } from "../../context/SiteData";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", cover_path: null, published: true };

const slugify = (s) =>
  s.toLowerCase()
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
    .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const BlogTab = () => {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/admin/blog").then(({ data }) => setPosts(data.posts)).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", {}, { timeout: 180000 });
      toast.success(`${data.created} yeni yazı GPT ile üretildi ve yayınlandı`);
      load();
    } catch {
      toast.error("Üretim başarısız, tekrar deneyin");
    } finally {
      setGenerating(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) {
        await api.put(`/admin/blog/${editing.id}`, editing);
        toast.success("Yazı güncellendi");
      } else {
        await api.post("/admin/blog", editing);
        toast.success("Yazı yayınlandı");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const remove = (id) => {
    setConfirm({
      message: "Yazı silinsin mi?",
      confirmLabel: "Evet, Sil",
      action: async () => {
        await api.delete(`/admin/blog/${id}`);
        toast.success("Yazı silindi");
        load();
      },
    });
  };

  if (!posts) return <p className="text-sm text-ink-500">Yükleniyor...</p>;

  if (editing) {
    return (
      <form onSubmit={save} data-testid="blog-editor" className="max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-ink-900">{editing.id ? "Yazıyı Düzenle" : "Yeni Yazı"}</h1>
          <button type="button" onClick={() => setEditing(null)} className="text-xs font-semibold uppercase tracking-[0.14em] text-ink-400 hover:text-ink-900" data-testid="blog-cancel">
            Vazgeç
          </button>
        </div>
        <div className="mt-6 space-y-5 border border-line bg-white p-6 sm:p-8">
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">Başlık *</label>
            <input required value={editing.title} data-testid="blog-input-title"
              onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })}
              className="input-field" placeholder="Yazı başlığı" />
          </div>
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">URL (slug)</label>
            <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} className="input-field" data-testid="blog-input-slug" />
          </div>
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">Kısa Özet</label>
            <input value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="input-field" data-testid="blog-input-excerpt" placeholder="Listelemede görünen 1-2 cümle" />
          </div>
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">İçerik *</label>
            <textarea required rows={10} value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="input-field resize-y" data-testid="blog-input-content" placeholder="Yazı içeriği" />
          </div>
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">Kapak Görseli</label>
            {editing.cover_path && (
              <img src={fileUrl(editing.cover_path)} alt="Kapak" className="mb-3 aspect-[16/9] w-full max-w-md object-cover" />
            )}
            <UploadZone testid="blog-cover-upload" onUploaded={(path) => setEditing({ ...editing, cover_path: path })} label="Kapak görselini sürükleyin" />
          </div>
          <label className="flex items-center gap-3 text-sm font-medium text-ink-800">
            <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="blog-input-published" className="h-4 w-4 accent-ink-900" />
            Yayında
          </label>
        </div>
        <button type="submit" className="btn-primary mt-6" data-testid="blog-save">Kaydet</button>
      </form>
    );
  }

  return (
    <div data-testid="blog-tab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink-900 sm:text-3xl">Blog</h1>
          <p className="mt-2 text-sm text-ink-500">Sitede /blog altında yayınlanan yazılar.</p>
        </div>
        <div className="flex shrink-0 gap-3">
          <button onClick={generate} disabled={generating} className="btn-outline-dark disabled:opacity-60" data-testid="blog-generate">
            <Sparkles size={15} />
            {generating ? "GPT yazıyor..." : "GPT ile Üret"}
          </button>
          <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="blog-new">
            <Plus size={15} /> Yeni Yazı
          </button>
        </div>
      </div>
      <div className="mt-8 space-y-px border border-line bg-line">
        {posts.length === 0 && <div className="bg-white p-10 text-center text-sm text-ink-400">Henüz yazı yok. İlk yazıyı ekleyin.</div>}
        {posts.map((p) => (
          <div key={p.id} className="card-lift flex items-center justify-between gap-4 bg-white p-5" data-testid={`blog-item-${p.slug}`}>
            <div className="flex min-w-0 items-center gap-4">
              {p.cover_path && <img src={fileUrl(p.cover_path)} alt="" className="hidden h-12 w-16 shrink-0 object-cover sm:block" />}
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-ink-900">{p.title}</p>
                <p className="mt-0.5 text-xs text-ink-400">/blog/{p.slug} · {p.published ? "Yayında" : "Taslak"}</p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => setEditing(p)} className="icon-btn border border-line p-2 text-ink-500 hover:border-ink-900 hover:text-ink-900" data-testid={`blog-edit-${p.slug}`} aria-label="Düzenle">
                <Pencil size={14} />
              </button>
              <button onClick={() => remove(p.id)} className="icon-btn border border-line p-2 text-ink-500 hover:border-ink-900 hover:text-ink-900" data-testid={`blog-delete-${p.slug}`} aria-label="Sil">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!confirm}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          const action = confirm?.action;
          setConfirm(null);
          if (action) await action();
        }}
      />
    </div>
  );
};

export default BlogTab;
